import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, getAuthHeader, handleResponse, images } from '../../../utils/api';

export function useSelectLot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ auctionId, lotId, maxPrice, notes }) => {
      const headers = await getAuthHeader('POST');
      const response = await fetch(`${API_BASE_URL}/auctions/system/${auctionId}/lots/${lotId}/select`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ maxPrice, notes }),
        credentials: 'include'
      });
      const data = await handleResponse(response);
      return { auctionId, lotId, data };
    },
    onMutate: async ({ auctionId, lotId }) => {
      // Cancel both system auctions and main auctions queries
      await Promise.all([
        queryClient.cancelQueries(['system-auctions', auctionId, 'lots']),
        queryClient.cancelQueries(['auctions'])
      ]);
      const previousData = {
        systemAuction: queryClient.getQueryData(['system-auctions', auctionId, 'lots']),
        auctions: queryClient.getQueryData(['auctions'])
      };

      // Optimistically update with absolute minimal object creation
      queryClient.setQueryData(['system-auctions', auctionId, 'lots'], old => {
        if (!old?.lots) return old;
        
        const lotIndex = old.lots.findIndex(lot => lot.id === lotId);
        if (lotIndex === -1) return old;

        // Create new array only if we need to update
        const updatedLot = {
          ...old.lots[lotIndex],
          user_selection: true
        };

        // Preserve the same array reference if the lot hasn't actually changed
        if (old.lots[lotIndex].user_selection === true) return old;

        // Create new array only if we need to update
        const updatedLots = [...old.lots];
        updatedLots[lotIndex] = updatedLot;

        // Only create new object if lots array changed
        return {
          ...old,
          lots: updatedLots
        };
      });

      return { previousData };
    },
    onError: (err, { auctionId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['system-auctions', auctionId, 'lots'], context.previousData.systemAuction);
        queryClient.setQueryData(['auctions'], context.previousData.auctions);
      }
    },
    onSuccess: async (_, { auctionId }) => {
      // Invalidate and refetch both system auctions and main auctions
      await Promise.all([
        queryClient.invalidateQueries(['system-auctions', auctionId, 'lots']),
        queryClient.invalidateQueries(['auctions'])
      ]);
      await Promise.all([
        queryClient.refetchQueries(['system-auctions', auctionId, 'lots']),
        queryClient.refetchQueries(['auctions'])
      ]);
    }
  });
}

export function useDeselectLot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ auctionId, lotId }) => {
      const headers = await getAuthHeader('DELETE');
      const response = await fetch(`${API_BASE_URL}/auctions/system/${auctionId}/lots/${lotId}/select`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      const data = await handleResponse(response);
      return { auctionId, lotId, data };
    },
    onMutate: async ({ auctionId, lotId }) => {
      // Cancel both system auctions and main auctions queries
      await Promise.all([
        queryClient.cancelQueries(['system-auctions', auctionId, 'lots']),
        queryClient.cancelQueries(['auctions'])
      ]);
      const previousData = {
        systemAuction: queryClient.getQueryData(['system-auctions', auctionId, 'lots']),
        auctions: queryClient.getQueryData(['auctions'])
      };

      // Optimistically update with absolute minimal object creation
      queryClient.setQueryData(['system-auctions', auctionId, 'lots'], old => {
        if (!old?.lots) return old;
        
        const lotIndex = old.lots.findIndex(lot => lot.id === lotId);
        if (lotIndex === -1) return old;

        // Create new array only if we need to update
        const updatedLot = {
          ...old.lots[lotIndex],
          user_selection: false
        };

        // Preserve the same array reference if the lot hasn't actually changed
        if (old.lots[lotIndex].user_selection === false) return old;

        // Create new array only if we need to update
        const updatedLots = [...old.lots];
        updatedLots[lotIndex] = updatedLot;

        // Only create new object if lots array changed
        return {
          ...old,
          lots: updatedLots
        };
      });

      return { previousData };
    },
    onError: (err, { auctionId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['system-auctions', auctionId, 'lots'], context.previousData.systemAuction);
        queryClient.setQueryData(['auctions'], context.previousData.auctions);
      }
    },
    onSuccess: async (_, { auctionId }) => {
      // Invalidate and refetch both system auctions and main auctions
      await Promise.all([
        queryClient.invalidateQueries(['system-auctions', auctionId, 'lots']),
        queryClient.invalidateQueries(['auctions'])
      ]);
      await Promise.all([
        queryClient.refetchQueries(['system-auctions', auctionId, 'lots']),
        queryClient.refetchQueries(['auctions'])
      ]);
    }
  });
}

export function useSystemAuctionBrowser() {
  return useQuery({
    queryKey: ['system-auctions', 'browse'],
    queryFn: async () => {
      const headers = await getAuthHeader('GET');
      const response = await fetch(`${API_BASE_URL}/auctions/system`, {
        headers,
        credentials: 'include'
      });
      return handleResponse(response);
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: 'if-stale' // Refetch on mount if data is stale
  });
}

export function useSystemAuctionLots(auctionId) {
  return useQuery({
    queryKey: ['system-auctions', auctionId, 'lots'],
    queryFn: async () => {
      if (!auctionId) return null;
      const headers = await getAuthHeader('GET');
      const response = await fetch(`${API_BASE_URL}/auctions/system/${auctionId}`, {
        headers,
        credentials: 'include'
      });
      const data = await handleResponse(response);
      return data;
    },
    enabled: !!auctionId,
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: 'if-stale' // Refetch on mount if data is stale
  });
}

export function useSelectAuction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, contactId, currency }) => {
      // First make a GET request to ensure we have a fresh CSRF token
      const getHeaders = await getAuthHeader('GET');
      await fetch(`${API_BASE_URL}/auctions/system/${id}`, {
        headers: getHeaders,
        credentials: 'include'
      });

      // Now make the POST request with the fresh token
      const headers = await getAuthHeader('POST');
      const response = await fetch(`${API_BASE_URL}/auctions/system/${id}/select`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ contactId, currency }),
        credentials: 'include'
      });
      return handleResponse(response);
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries(['system-auctions', 'browse']);
      const previousData = queryClient.getQueryData(['system-auctions', 'browse']);

      // Optimistically update the auction's is_selected status
      queryClient.setQueryData(['system-auctions', 'browse'], old => {
        if (!old) return old;
        return old.map(auction => 
          auction.id === id 
            ? { ...auction, is_selected: true }
            : auction
        );
      });

      return { previousData };
    },
    onError: (err, { id }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['system-auctions', 'browse'], context.previousData);
      }
    },
    onSuccess: () => {
      // Still invalidate queries to ensure data consistency
      queryClient.invalidateQueries(['system-auctions', 'browse']);
      queryClient.invalidateQueries(['auctions']);
    }
  });
}
